import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import type { Project } from '@shared/types'
import { StatsSkeleton } from '../components/ui/Skeletons'
import { StatusBadge } from '../components/ui/StatusBadge'

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!slug) return
    api.get<Project>(`/projects/${slug}`)
      .then(setProject)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) return <StatsSkeleton count={3} />
  if (!project) return <div style={{ color: 'var(--text-secondary)', padding: 24, fontSize: 14 }}>Project not found.</div>

  const budget = project.budget_total ?? 0
  const actual = project.actual_cost_total ?? 0
  const margin = budget > 0 ? ((budget - actual) / budget * 100) : 0

  return (
    <div className="page-enter">
      <div className="mb-6">
        <Link to="/projects" style={{ fontSize: 13, color: 'var(--text-secondary)' }} className="hover:underline">&larr; Back to Projects</Link>
      </div>

      <div className="page-header">
        <div>
          <h1 className="page-title">{project.name}</h1>
          <StatusBadge status={project.status} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="card p-4">
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, letterSpacing: '0.02em' }}>Budget</p>
          <p className="mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{formatCurrency(budget)}</p>
        </div>
        <div className="card p-4">
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, letterSpacing: '0.02em' }}>Actual Cost</p>
          <p className="mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{formatCurrency(actual)}</p>
        </div>
        <div className="card p-4">
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, letterSpacing: '0.02em' }}>Margin</p>
          <p className="mono" style={{ fontSize: 18, fontWeight: 600, color: margin >= 0 ? 'var(--success)' : 'var(--danger)', fontFamily: 'var(--font-mono)' }}>
            {margin.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-4">
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>Details</h3>
          <dl className="space-y-2" style={{ fontSize: 14 }}>
            {project.phase && (
              <div className="flex justify-between">
                <dt style={{ color: 'var(--text-secondary)' }}>Phase</dt>
                <dd style={{ color: 'var(--text)' }}>{project.phase}</dd>
              </div>
            )}
            {project.start_date && (
              <div className="flex justify-between">
                <dt style={{ color: 'var(--text-secondary)' }}>Start Date</dt>
                <dd className="mono" style={{ color: 'var(--text)' }}>{new Date(project.start_date).toLocaleDateString()}</dd>
              </div>
            )}
            {project.end_date && (
              <div className="flex justify-between">
                <dt style={{ color: 'var(--text-secondary)' }}>End Date</dt>
                <dd className="mono" style={{ color: 'var(--text)' }}>{new Date(project.end_date).toLocaleDateString()}</dd>
              </div>
            )}
            {project.site_address && (
              <div className="flex justify-between">
                <dt style={{ color: 'var(--text-secondary)' }}>Site</dt>
                <dd style={{ color: 'var(--text)' }}>{project.site_address}</dd>
              </div>
            )}
          </dl>
        </div>

        {project.notes && (
          <div className="card p-4">
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Notes</h3>
            <p className="whitespace-pre-wrap" style={{ fontSize: 14, color: 'var(--text)' }}>{project.notes}</p>
          </div>
        )}
      </div>
    </div>
  )
}
